import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Server, Terminal, Layers, ArrowRight, CheckCircle, Dna, Network, Database, ExternalLink, HardDrive, Box, Award, Zap, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import ServerAdminTechnologies from '../../components/services/ServerAdminTechnologies';

const SectionTitle = ({ subtitle, children, isLight = false }: { subtitle: string, children: React.ReactNode, isLight?: boolean }) => (
    <div className="text-center mb-16 max-w-4xl mx-auto px-4">
        <h2 className={`text-4xl md:text-5xl font-bold tracking-tighter mb-4 ${isLight ? 'text-white' : 'text-slate-900'}`}>{children}</h2>
        <p className={`text-lg font-light ${isLight ? 'text-slate-300' : 'text-slate-600'}`}>{subtitle}</p>
    </div>
);

const heroSlides = [
    {
        title: "Enterprise-Grade Server Administration",
        description: "Built for unparalleled security, stability, and scale across Windows, Linux, and Virtualization environments.",
        image: "/images/Server administration 4.png",
        linkText: "Explore Enterprise Solutions",
        linkTo: "#core-services"
    },
    {
        title: "Fortified Server Ecosystems",
        description: "Proactive defense and continuous hardening for your mission-critical Windows and Linux infrastructure.",
        image: "/images/Server administration 3.png",
        linkText: "Secure Your Servers",
        linkTo: "/contact"
    },
    {
        title: "Optimized Performance, Unrivaled Uptime",
        description: "Maximizing the efficiency and availability of your critical systems through expert Linux, Virtualization, and Storage management.",
        image: "/images/Server administration 2.png",
        linkText: "Boost Performance Now",
        linkTo: "#core-services"
    }
];

const coreServices = [
    {
        title: "Windows Server Administration",
        subtitle: "Enterprise Foundation",
        icon: (
            <svg viewBox="0 0 24 24" className="h-10 w-10 text-blue-500" fill="currentColor">
                <path d="m2 5.24l7.98.02v6.23H2v-6.25zM10.98 5.26v6.23h10.95V5.26h-10.95zM2 12.47h7.98v6.25H2v-6.25zM10.98 12.47v6.25h10.95v-6.25h-10.95z"/>
            </svg>
        ),
        image: "/images/Server administration 4.png",
        description: "Comprehensive management for the core of your corporate IT environments, focusing on identity, access control, and centralized governance to ensure corporate stability.",
        features: [
            "Active Directory design, deployment & administration",
            "Domain architecture & trust relationships",
            "DNS and DHCP services management",
            "Group Policy (GPO) design and enforcement",
            "WSUS (centralized update management)",
            "WDS / MDT (automated system deployment)",
            "File and print services configuration",
            "Identity lifecycle management",
            "Domain security hardening & auditing"
        ]
    },
    {
        title: "Linux Server Administration",
        subtitle: "Performance & Critical Workloads",
        icon: <Terminal className="h-10 w-10 text-blue-500" />,
        image: "/images/Server administration 2.png",
        description: "Managing the high-performance engine of your infrastructure, optimized for uptime, critical workloads, and scalability across leading distributions.",
        features: [
            "Secure OS installation & baseline hardening (RHEL, AlmaLinux, Ubuntu, Debian)",
            "Patch and lifecycle management",
            "Storage management (RAID, LVM, file systems)",
            "User and permission management",
            "Performance tuning and optimization",
            "Backup and disaster recovery solutions",
            "Comprehensive logging, monitoring, and auditing"
        ]
    },
    {
        title: "Virtualization & Platform Engineering",
        subtitle: "Resilience Layer",
        icon: <Layers className="h-10 w-10 text-blue-500" />,
        image: "/images/Server administration 3.png",
        description: "Building the critical resilience layer of your infrastructure, ensuring high availability, scalability, and rapid disaster recovery for continuous business operations.",
        features: [
            "Hypervisor deployment and hardening (Proxmox VE, VMware vSphere, Nutanix)",
            "Virtual machine lifecycle management",
            "High availability and clustering configuration",
            "Resource optimization and capacity planning",            "Backup, snapshot, and recovery strategies",
            "Infrastructure scalability planning and implementation"
        ]
    },
    {
        title: "Server Racking, Mounting & Precision Deployment",
        subtitle: "Hardware Lifecycle Management",
        icon: <Box className="h-10 w-10 text-blue-500" />,
        image: "/images/Server administration 6.png",
        description: "The integrity of your digital operations begins with the physical foundation. We provide end-to-end hardware lifecycle management, from strategic procurement and reselling to precision racking and low-level system validation.",
        features: [
            "Enterprise Hardware Reselling",
            "Precision Cabinet Mounting",
            "Structured Cabling & Labels",
            "BIOS/UEFI & IPMI Configuration",
            "RAID Array Initialization",
            "Hardware-Level Validation",
            "Thermal & Power Planning",
            "Firmware Baselining"
        ]
    }
];

const whyAtlasDefenders = [
    {
        icon: <Award />,
        title: "25+ Years of Experience",
        description: "Of cumulative, hands-on experience in enterprise operations and cybersecurity."
    },
    {
        icon: <Zap />,
        title: "Production-Focused",
        description: "We manage live, mission-critical systems. Our solutions are battle-tested, not theoretical."
    },
    {
        icon: <Users />,
        title: "Unified Expertise",
        description: "We are a single, cohesive team of infrastructure and security experts, not just IT administrators."
    }
];

const ServerAdministrationPage: React.FC = () => {
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }, 6000);
        return () => clearInterval(timer);
    }, []);

    const fadeIn = { 
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } }
    };

    return (
        <div className="bg-white text-slate-800">
            {/* Redesigned Hero Section with Content Rotation */}
            <section className="relative w-full bg-[#060606] overflow-hidden min-h-[90vh] flex flex-col lg:flex-row">
                
                {/* Left Content Side - Animated */}
                <div className="flex-1 flex items-center justify-start p-8 md:p-16 lg:pl-24 xl:pl-32 z-20">
                    <div className="max-w-xl w-full">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentSlide}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.8, ease: "easeInOut" }}
                            >
                                <motion.h1
                                    className="text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-white mb-6 leading-[1.1]"
                                >
                                    {heroSlides[currentSlide].title}
                                </motion.h1>
                                <motion.p 
                                    className="text-xl md:text-2xl text-slate-300 leading-relaxed font-light mb-10 max-w-lg"
                                >
                                    {heroSlides[currentSlide].description}
                                </motion.p>
                            </motion.div>
                        </AnimatePresence>

                        <motion.div 
                            initial={{ opacity: 0, y: 20 }} 
                            animate={{ opacity: 1, y: 0 }} 
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="flex flex-wrap gap-4"
                        >
                            <Link 
                              to="/contact" 
                              className="bg-[#0076ce] hover:bg-[#005da6] text-white font-semibold py-4 px-10 transition-all duration-300 rounded-sm shadow-lg hover:shadow-[#0076ce]/20"
                            >
                                Start Your Transformation
                            </Link>
                            <a 
                              href="#core-services" 
                              className="border border-white/30 hover:border-white hover:bg-white/10 text-white font-semibold py-4 px-10 transition-all duration-300 rounded-sm backdrop-blur-sm"
                            >
                                View Solutions
                            </a>
                        </motion.div>

                        {/* Slide Indicators */}
                        <div className="flex gap-3 mt-12">
                            {heroSlides.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentSlide(index)}
                                    className={`h-1 transition-all duration-500 rounded-full ${index === currentSlide ? 'w-12 bg-[#0076ce]' : 'w-6 bg-white/20'}`}
                                    aria-label={`Go to slide ${index + 1}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Image Side - Animated */}
                <div className="flex-[1.4] relative min-h-[500px] lg:min-h-0 overflow-hidden">
                    <AnimatePresence mode="wait">
                        <motion.div 
                            key={currentSlide}
                            className="absolute inset-0 bg-cover bg-center"
                            style={{ backgroundImage: `url('${heroSlides[currentSlide].image}')` }}
                            initial={{ opacity: 0, scale: 1.1 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 1.05 }}
                            transition={{ duration: 1.2, ease: "easeOut" }}
                        />
                    </AnimatePresence>
                    
                    {/* Gradient overlays to blend */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#060606] via-transparent to-transparent lg:bg-gradient-to-r lg:from-[#060606] lg:to-transparent lg:via-[#060606]/40 z-10"></div>
                </div>
            </section>

            {/* What Server Administration Means - Dell Style Blue Theme */}
            <section className="py-20 md:py-32 bg-[#00204a] text-white overflow-hidden">
                <div className="container mx-auto px-4 lg:px-12 xl:px-24">
                    <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
                        {/* Left Side: Content */}
                        <div className="lg:w-5/12 flex flex-col justify-center">
                            <motion.span 
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                className="text-sm font-medium tracking-widest text-white mb-6 uppercase"
                            >
                                THE FOUNDATION OF OPERATIONS
                            </motion.span>
                            <motion.h2 
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.1 }}
                                className="text-4xl md:text-5xl lg:text-6xl font-light mb-8 leading-tight tracking-tight text-white"
                            >
                                Reliability. Performance. Security.
                            </motion.h2>
                            <motion.div 
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.2 }}
                                className="space-y-6 text-blue-100/90 text-lg md:text-xl font-light leading-relaxed mb-10"
                            >
                                <p>
                                    Servers are the engines of modern business. Improper administration directly translates to business risk: downtime, security incidents, and data loss.
                                </p>
                                <p>
                                    Professional server administration is the discipline of eliminating these risks and ensuring your infrastructure is resilient and ready for growth.
                                </p>
                            </motion.div>
                            <motion.div 
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.3 }}
                                className="flex flex-wrap items-center gap-8"
                            >
                                <a 
                                    href="#core-services" 
                                    className="text-white hover:underline flex items-center gap-2 group text-lg font-medium"
                                >
                                    Get Started <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                                </a>
                            </motion.div>
                        </div>

                        {/* Right Side: Image */}
                        <div className="lg:w-7/12 relative">
                            <motion.div
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 1, ease: "easeOut" }}
                                className="relative z-10 p-2 bg-white/5 backdrop-blur-sm rounded-sm"
                            >
                                <img 
                                    src="/Logos/Datacenter1.jpg" 
                                    alt="Professional Server Administration" 
                                    className="w-full h-auto rounded-sm shadow-2xl"
                                />
                            </motion.div>
                            {/* Decorative gradient behind image */}
                            <div className="absolute -inset-4 bg-gradient-to-tr from-blue-600/20 to-transparent blur-3xl -z-10 rounded-full"></div>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* Core Services Section - Unified Dark Theme */}
            <section id="core-services" className="py-20 md:py-28 bg-slate-900">
                <div className="container mx-auto px-4">
                    <SectionTitle subtitle="Our services support the full lifecycle of enterprise server environments." isLight>
                        Core Server Administration Services
                    </SectionTitle>
                    <div className="space-y-20 lg:space-y-28">
                        {coreServices.map((service, index) => (
                            <motion.div
                                key={service.title}
                                className={`flex flex-col lg:flex-row gap-12 items-center ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.2 }}
                                variants={{
                                    hidden: { opacity: 0, y: 50 },
                                    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
                                }}
                            >
                                <div className="lg:w-1/2">
                                    <motion.img
                                        src={service.image}
                                        alt={service.title}
                                        className="rounded-xl shadow-2xl border-8 border-slate-800 w-full h-auto object-cover"
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.8, ease: "easeOut" }}
                                    />
                                </div>
                                <div className="lg:w-1/2 text-white">
                                    <div className="flex items-center mb-4">
                                        {service.icon}
                                        <h3 className="text-3xl font-bold ml-4 text-white">{service.title}</h3>
                                    </div>
                                    <p className="text-blue-300 text-lg font-light mb-4">{service.subtitle}</p>
                                    <p className="text-slate-300 leading-relaxed mb-6 font-light">{service.description}</p>
                                    <ul className="space-y-3 mb-6">
                                        {service.features.map((feature, i) => (
                                            <li key={i} className="flex items-start text-slate-200">
                                                <CheckCircle className="h-5 w-5 text-blue-400 mr-3 mt-1 flex-shrink-0" />
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <ServerAdminTechnologies />

            {/* Why Atlas Defenders Section - Lenovo Style */}
            <section className="py-20 md:py-28 bg-white">
                <div className="container mx-auto px-4">
                    <SectionTitle subtitle="Experience that matters where it counts: in production.">
                        Why Atlas Defenders?
                    </SectionTitle>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-6xl mx-auto">
                        {whyAtlasDefenders.map((item, index) => (
                            <div key={index} className="text-center flex flex-col items-center">
                                <div className="mb-5 text-blue-600">
                                    {React.cloneElement(item.icon, { size: 48, strokeWidth: 1.5 })}
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900 mb-4">{item.title}</h3>
                                <p className="text-slate-600 text-lg mb-6 flex-grow">{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            
            {/* CTA Section with Background Image */}
            <section 
                className="relative py-28 md:py-36 bg-cover bg-center bg-no-repeat overflow-hidden"
                style={{ backgroundImage: `url('/images/Server administration 6.png')` }}
            >
                <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"></div>
                <div className="container mx-auto px-4 relative z-20">
                    <div className="flex flex-col items-center text-center">
                        <div className="lg:w-2/3">
                            <h2 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight tracking-tight">
                                Discuss Your Infrastructure Strategy
                            </h2>
                            <p className="text-lg text-slate-200 max-w-2xl mx-auto mb-10 font-light leading-relaxed">
                                Engage our senior engineers for a comprehensive review of your server environment. Let's explore opportunities for enhanced security, stability, and performance.
                            </p>
                            <Link
                                to="/contact"
                                className="inline-flex items-center justify-center gap-3 text-white font-bold py-4 px-8 border-2 border-white hover:bg-white hover:text-slate-900 transition-all duration-300 group text-lg transform hover:scale-105"
                            >
                                <span>Request a Technical Review</span>
                                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ServerAdministrationPage;
